import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalSwitch, MinimalTimePicker } from '@ovasabi/ui-minimal'
import {
  dayLabel,
  isValidDay,
  to12h,
  type WeeklySchedule,
  type WorkingDay,
} from '../../lib/vendorSchedule'

/**
 * Weekly working-hours editor for a chef/vendor. Composes ui-minimal primitives
 * (a day toggle + two time dropdowns per row) rather than raw controls, so it
 * inherits the theme, focus rings, and keyboard nav. Purely controlled: it owns
 * no state, emitting the next `WeeklySchedule` on every change so the host page
 * decides when to persist.
 */
export interface WeeklyScheduleEditorProps {
  value: WeeklySchedule
  onChange: (next: WeeklySchedule) => void
  disabled?: boolean
}

const addWallMinutes = (value: string, delta: number): string => {
  const [hours, minutes] = value.split(':').map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '00:30'
  const total = Math.min(23 * 60 + 59, hours * 60 + minutes + delta)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/*
 * Density: a working week is seven rows the chef scans, not seven forms they
 * fill in. The day, its hours and its toggle share one line, and the two time
 * dropdowns appear underneath only for the days that are actually open — so a
 * typical week (one or two closed days) is a short list rather than a screen
 * and a half of stacked controls.
 */
const Style = {
  Wrap: styled.div`
    display: grid;
    width: 100%;
    min-width: 0;
  `,
  Row: styled.div<{ $invalid: boolean }>`
    display: grid;
    min-width: 0;
    gap: 8px;
    padding: 10px 0;
    border-top: 1px solid ${minimalVars.color.borderSubtle};
    &:first-child {
      border-top: 0;
      padding-top: 0;
    }
    color: ${({ $invalid }) => ($invalid ? minimalVars.color.danger : minimalVars.color.textPrimary)};
  `,
  RowHeader: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    min-width: 0;
  `,
  DayCopy: styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  `,
  Day: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.captionSize};
  `,
  DayState: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  Times: styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${minimalVars.space['2xs']};
    width: 100%;
    min-width: 0;

    & > [data-minimal='TimePicker'] {
      min-width: 0;
    }

    /* 360px matches no named stop; it sits under the hand stop. */
    @media (max-width: 360px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
}

export const WeeklyScheduleEditor = ({
  value,
  onChange,
  disabled = false,
}: WeeklyScheduleEditorProps) => {
  const rows = value.days

  const patchDay = (index: number, patch: Partial<WorkingDay>) => {
    const days = rows.map((d, i) => (i === index ? { ...d, ...patch } : d))
    onChange({ ...value, days })
  }

  return (
    <Style.Wrap role="group" aria-label="Weekly working hours">
      {rows.map((d, index) => {
        const invalid = !isValidDay(d)
        return (
          <Style.Row key={d.day} $invalid={invalid} role="group" aria-label={dayLabel(d.day, 'long')}>
            <Style.RowHeader>
              <Style.DayCopy>
                <Style.Day>{dayLabel(d.day, 'long')}</Style.Day>
                <Style.DayState>{d.open ? `${to12h(d.openTime)} – ${to12h(d.closeTime)}` : 'Closed'}</Style.DayState>
              </Style.DayCopy>
              <MinimalSwitch
                label={d.open ? 'Open' : 'Closed'}
                checked={d.open}
                disabled={disabled}
                onCheckedChange={() =>
                  patchDay(index, {
                    open: !d.open,
                    // Seed sane hours the first time a day is switched on.
                    openTime: d.openTime || '09:00',
                    closeTime: d.closeTime || '17:00',
                  })
                }
              />
            </Style.RowHeader>
            {d.open ? (
              <Style.Times>
                <MinimalTimePicker
                  label="Opens"
                  value={d.openTime}
                  onChange={(next) => patchDay(index, { openTime: next })}
                  intervalMinutes={30}
                  maxTime="23:00"
                  disabled={disabled}
                />
                <MinimalTimePicker
                  label="Closes"
                  value={d.closeTime}
                  onChange={(next) => patchDay(index, { closeTime: next })}
                  minTime={addWallMinutes(d.openTime, 30)}
                  intervalMinutes={30}
                  error={invalid ? 'Closing time must be later than opening time.' : undefined}
                  disabled={disabled}
                />
              </Style.Times>
            ) : null}
          </Style.Row>
        )
      })}
    </Style.Wrap>
  )
}
